import React, {FC} from "react";
import {Checkbox, Form, Input, Radio} from "antd";
import {
    normalizePublicRegistrationOptionQuestionType,
    orderPublicRegistrationOptionsByDependency
} from "./publicRegistrationModel";
import {useRegistrationCopy} from "./registrationCopy";
import type {PublicRegistrationOption} from "./types";

const ActivityAnswerField: FC<{
    option: PublicRegistrationOption;
    namePrefix: Array<string | number>;
}> = ({option, namePrefix}) => {
    const {registrationOptionLabel, registrationOptionChoiceLabel} = useRegistrationCopy();
    const questionType = normalizePublicRegistrationOptionQuestionType(option.questionType);
    const choices = (option.choices ?? [])
        .slice()
        .sort((left, right) => Number(left.order ?? 0) - Number(right.order ?? 0) || left.id - right.id)
        .map(choice => ({
            value: choice.id,
            label: registrationOptionChoiceLabel(choice)
        }));

    return (
        <Form.Item
            name={[...namePrefix, String(option.id)]}
            label={registrationOptionLabel(option)}
        >
            {questionType === "SingleChoice" ? (
                <Radio.Group options={choices}/>
            ) : questionType === "MultiChoice" ? (
                <Checkbox.Group options={choices}/>
            ) : (
                <Input.TextArea rows={3}/>
            )}
        </Form.Item>
    );
};

// Yes/no activities as one checkbox group (dependents indented under their parent),
// then one field per choice/text activity.
export const ActivityFields: FC<{
    options: PublicRegistrationOption[];
    selectedOptionIdsName: Array<string | number>;
    activityAnswersName: Array<string | number>;
}> = ({options, selectedOptionIdsName, activityAnswersName}) => {
    const {labels, registrationOptionLabel} = useRegistrationCopy();
    const yesNoOptions = orderPublicRegistrationOptionsByDependency(options)
        .filter(option => normalizePublicRegistrationOptionQuestionType(option.questionType) === "YesNo");
    const answerOptions = options
        .filter(option => normalizePublicRegistrationOptionQuestionType(option.questionType) !== "YesNo");

    return (
        <>
            {yesNoOptions.length > 0 ? (
                <Form.Item name={selectedOptionIdsName} label={labels.additionalOptions}>
                    <Checkbox.Group
                        options={yesNoOptions.map(option => ({
                            value: option.id,
                            label: registrationOptionLabel(option),
                            className: option.visibilityDependsOnEventOptionId
                                ? "public-registration-dependent-option"
                                : undefined
                        }))}
                    />
                </Form.Item>
            ) : null}

            {answerOptions.map(option => (
                <ActivityAnswerField key={option.id} option={option} namePrefix={activityAnswersName}/>
            ))}
        </>
    );
};
